import { useState } from "react";
import { useRouter } from "next/router";
import Link from "next/link";
import { toast } from "sonner";

import { useProfile } from "@/context/ProfileContext";
import { submitEventTakedownAppeal, submitAccountSuspensionAppeal } from "@/services/appealService";
import { getErrorMessage } from "@/services/apiClient";
import { ROUTE } from "@/route/app_routes.js";

/**
 * The single destination every appeal-related email/notification links to
 * (EventTakenDownNotification / AccountSuspendedNotification both build
 * "/appeal?type=event_takedown&event={id}" or "/appeal?type=account_suspension") -- one page,
 * two modes, picked by `type`:
 *  - event_takedown: the organizer is still logged in (only a 30-day posting ban applies, their
 *    account itself isn't touched), so this goes through the normal authenticated dashboard
 *    proxy. Just a message -- which event is obvious from context.
 *  - account_suspension: the account's own Sanctum token was revoked the moment it was
 *    suspended (see Authenticate middleware), so this can never be an authenticated request --
 *    identity is re-verified with email+password instead (see AppealController::
 *    storeForSuspendedAccount), without ever issuing a new token.
 */
const AppealPage = () => {
  const router = useRouter();
  const { loggedIn, profile } = useProfile();
  const type = router.query.type === "account_suspension" ? "account_suspension" : "event_takedown";
  const eventId = router.query.event;

  const [message, setMessage] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!message.trim() || message.trim().length < 10) {
      toast.error("Please explain why you believe this was a mistake (at least 10 characters).");
      return;
    }
    setSubmitting(true);
    try {
      if (type === "event_takedown") {
        await submitEventTakedownAppeal(eventId, message.trim());
      } else {
        if (!email.trim() || !password) {
          toast.error("Email and password are both required.");
          setSubmitting(false);
          return;
        }
        await submitAccountSuspensionAppeal(email.trim(), password, message.trim());
      }
      setSubmitted(true);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="rbt-appeal-page">
        <div className="rbt-appeal-page__card">
          <div className="rbt-appeal-page__icon is-success"><i className="feather-check-circle"></i></div>
          <h1>Appeal submitted</h1>
          <p>An admin will review this and get back to you. You&apos;ll be notified{type === "event_takedown" ? " in-app and" : ""} by email once a decision is made.</p>
          <Link className="rbt-btn btn-gradient radius-round" href={ROUTE.home}>Back to home</Link>
        </div>
      </div>
    );
  }

  if (type === "event_takedown" && !loggedIn) {
    return (
      <div className="rbt-appeal-page">
        <div className="rbt-appeal-page__card">
          <div className="rbt-appeal-page__icon is-warning"><i className="feather-lock"></i></div>
          <h1>Please log in first</h1>
          <p>You need to be signed in to the account that organized this event to submit an appeal.</p>
          <Link className="rbt-btn btn-gradient radius-round" href={`${ROUTE.login}?returnTo=${encodeURIComponent(router.asPath)}`}>Log in</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="rbt-appeal-page">
      <div className="rbt-appeal-page__card">
        <div className="rbt-appeal-page__icon"><i className="feather-corner-up-left"></i></div>
        <h1>{type === "event_takedown" ? "Appeal this takedown" : "Appeal this suspension"}</h1>
        <p>
          {type === "event_takedown"
            ? "If you believe your event was taken down by mistake, explain why below. An admin will review it."
            : "If you believe your account was suspended by mistake, verify your identity and explain why below."}
        </p>

        <form onSubmit={handleSubmit}>
          {type === "account_suspension" && (
            <>
              <div className="rbt-appeal-page__field">
                <label htmlFor="appeal-email">Email</label>
                <input id="appeal-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
              <div className="rbt-appeal-page__field">
                <label htmlFor="appeal-password">Password</label>
                <input id="appeal-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </div>
            </>
          )}
          {type === "event_takedown" && profile && (
            <p style={{ fontSize: 13, color: "var(--color-body)", marginTop: -10 }}>
              Submitting as <strong>{profile.EMAIL}</strong>.
            </p>
          )}
          <div className="rbt-appeal-page__field">
            <label htmlFor="appeal-message">Why should this be reconsidered?</label>
            <textarea id="appeal-message" rows={5} value={message} onChange={(e) => setMessage(e.target.value)} required />
          </div>
          <button type="submit" className="rbt-btn btn-gradient radius-round w-100" disabled={submitting}>
            {submitting ? "Submitting…" : "Submit appeal"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AppealPage;
